import { useMemo } from "react";
import { validatorTaxId } from "../../../shared/validators/validatorTaxId";
import { DynamicFormEngineConfig } from "../../DynamicForm/context/DynamicFormEngineConfigContext";
import { useModalNeedToMakeAChange } from "../modals/ModalNeedToMakeAChange";
import { IFormSchemaField } from "../types";

/**
 * Employee-tax-forms binding of the domain-agnostic dynamic form engine
 * (DynamicFormEngineConfig). Everything US-tax-specific the renderer needs —
 * the SSN/ITIN format validator for MaskedId fields, which catalog Currency
 * fields the design renders without a "$", the tax draft namespace, and the
 * "Need to make a change?" explainer — is declared HERE, keeping the engine
 * reusable by any other surface.
 */

// Catalog fields typed `Currency` that the tax-forms design renders WITHOUT a
// "$" symbol — they read as plain values the contractor enters directly, not
// money. The forms catalog owns the field `type`, so this app-side override
// drops currency mode for these keys while keeping numeric input.
const FIELD_KEYS_WITHOUT_CURRENCY_SYMBOL: ReadonlySet<string> = new Set([
  "childCredits", // W-4 Step 3 — "Qualifying children under 17 × $2,000"
  "otherDependents" // W-4 Step 3 — "Other dependents × $500"
]);

// MaskedId on tax forms is the SSN/ITIN field: layer the shared tax-id format
// check on top of the schema's string rules. EntityType is Individual (these
// are employee-facing forms) and the jurisdiction country drives the format.
const maskedIdValidator: DynamicFormEngineConfig["maskedIdValidator"] = (
  field: IFormSchemaField,
  country: string
) => validatorTaxId(field.key, "Individual", country);

export const useEmployeeTaxFormsEngineConfig =
  (): Partial<DynamicFormEngineConfig> => {
    const modalNeedToMakeAChange = useModalNeedToMakeAChange();
    return useMemo(
      () => ({
        draftKeyNamespace: "etf",
        defaultCountry: "US",
        currencyCode: "USD",
        fieldKeysWithoutCurrencySymbol: FIELD_KEYS_WITHOUT_CURRENCY_SYMBOL,
        maskedIdValidator,
        onEditPrefilledData: () => modalNeedToMakeAChange.open({}),
        submitErrorContextKey: "EmployeeTaxFormSubmit"
      }),
      [modalNeedToMakeAChange]
    );
  };
